// Pure list logic for the CP Sourcing "Dormant CPs" view.
//
// The /dormant-cps endpoint, called once with tier="all", returns every
// assigned CP — dormant and active alike — each row carrying its own `tier`
// plus ownerId/ownerName (its CP Source). That one payload is therefore enough
// to drive the CP Source dropdown, the source filter, the search, all five tab
// counts and the tier selection without another round trip. These helpers do
// that work, kept pure and out of the component so they can be tested directly.
//
// The narrowing order matters and mirrors what the user sees: source + search
// narrow the dataset first, tier counts are computed over that narrowed set
// (so every tab count describes the current filter), and only then is the
// active tier selected for rendering.

const DORMANT_TIERS = ["outreach", "escalate", "critical", "neverActive"];

// CP Source dropdown options, in the panel's { id, label } shape: id is the
// owning sourcing user's id, label their name. Deduped and sorted by name.
export const collectSourceOptions = (rows) => {
  const byId = new Map();
  for (const r of rows || []) {
    if (r?.ownerId && r?.ownerName && !byId.has(r.ownerId)) {
      byId.set(r.ownerId, { id: r.ownerId, label: r.ownerName });
    }
  }
  return [...byId.values()].sort((a, b) => a.label.localeCompare(b.label));
};

// Apply the two narrowing controls. No sources ticked = no source constraint;
// blank search = no search constraint. The search fields and the
// case-insensitive substring match mirror the backend's own search so the two
// can't disagree (cpSourcingRoutes.js /dormant-cps).
export const narrowRows = (rows, { sources = [], search = "" } = {}) => {
  const q = String(search || "").trim().toLowerCase();
  return (rows || []).filter((r) => {
    if (sources.length > 0 && !sources.includes(r.ownerId)) return false;
    if (q === "") return true;
    return (
      String(r.cpName || "").toLowerCase().includes(q) ||
      String(r.cpDisplayId || "").toLowerCase().includes(q)
    );
  });
};

// Tab counts for the given rows. `all` is every row, active CPs included; the
// four dormant buckets count only their own tier. This is the backend's own
// contract for the `tiers` object, preserved so the tabs read the same as
// before — the difference is only that it is now computed after filtering.
export const countTiers = (rows) => {
  const counts = { all: 0, outreach: 0, escalate: 0, critical: 0, neverActive: 0 };
  for (const r of rows || []) {
    counts.all += 1;
    if (DORMANT_TIERS.includes(r?.tier)) counts[r.tier] += 1;
  }
  return counts;
};

// Rows for the selected tab. "all" keeps active CPs; any other tier is an
// exact match.
export const selectTier = (rows, tier) =>
  tier === "all" ? rows || [] : (rows || []).filter((r) => r.tier === tier);

// Load a complete result set out of an offset/limit endpoint.
//
// /dormant-cps caps `limit` at 100 server-side, so the full set arrives as
// ceil(total/pageSize) sequential requests. `fetchPage(offset, limit)` must
// resolve to { rows, total }; `total` is read from each response so a set that
// grew between pages is still followed to the end. An empty page ends the loop
// regardless of what `total` claims, so an overstated total cannot spin here
// forever.
export const fetchAllPages = async (fetchPage, pageSize) => {
  const rows = [];
  let total = 0;
  let offset = 0;
  for (;;) {
    const page = await fetchPage(offset, pageSize);
    const pageRows = page?.rows || [];
    total = page?.total ?? total;
    rows.push(...pageRows);
    if (pageRows.length === 0 || rows.length >= total) break;
    // Advance by what came back, not by what was asked for. The server caps
    // `limit` itself, so a page can be shorter than pageSize while more rows
    // remain — stepping by pageSize would skip straight over the difference
    // and drop CPs from the list with no error anywhere.
    offset += pageRows.length;
  }
  return { rows, total };
};

// Next rendered-row count as the reader scrolls, clamped to what exists. The
// scroll observer can fire more than once before React re-renders, so growing
// past the end has to settle at the total rather than climb forever.
export const growReveal = (current, step, total) =>
  Math.min(current + step, total);
